import { t } from '../i18n';
import get from 'lodash.get';
import {
  ConvertedConfiguration,
  ConvertedProduct,
  OptionConvertProductCart,
  DiscoveryBlock,
  CartOrderLine,
  PromotionApplyOnEnum,
} from '../types';
import {
  DiscoveryFilter,
  DiscoveryItem,
  DiscoveryFlashSale,
  DiscoveryProduct,
  DiscoveryPromotion,
  ProductDetailImage,
  ProductStatus,
  BenefitItems,
  ProductDetailProductGroup,
  ProductDetailProductGroupVariant,
  ProductDetailProductGroupConfiguration,
  ProductDetailProductGroupVariantAttrValue,
  PromotionWithPrice,
  DiscoveryPrice,
} from '@teko/discovery';
import { checkExpiryDateTimestamp, getRemainingTime } from './common';
import {
  BannerEntity,
  ProductCardProps,
  ProductPromotion,
  MoneyPromotion,
  DisplayStatus,
  TProductVariant,
} from '@teko/ecommerce';
import { convertPrice, getFrame } from './products';
import { LinkProps } from 'next/link';
import slug from '@teko/slug';
import { TagProps } from '@teko/ui';
import {
  ProductCardButton,
  ProductCardProps as NewProductCardProps,
  ProductTag,
  ProductPriceProps,
} from '@teko/types/ecommerce';
import { getTime } from './datetime';
import { PRODUCT_DETAIL_PAGE } from '../constants';
import { getTrackingData, PromoteBlockProps } from '@teko/teko-ecommerce';
import { needConfirm18YearsOld } from './cart';
const listingAndDiscoveryKeysMap: any = {
  priceGte: 'price_gte',
  priceLte: 'price_lte',
};
const listingAndDiscoveryValuesMap: any = {
  ORDER_BY_DESCENDING: 'DESC',
  ORDER_BY_ASCENDING: 'ASC',
};
const cleanObject = (object: any) => {
  const newObj: any = {};
  Object.keys(object).forEach((key) => {
    if (object[key]) {
      if (
        !Array.isArray(object[key]) ||
        (Array.isArray(object[key]) && object[key].length)
      ) {
        newObj[key] = object[key];
      }
    }
  });
  return newObj;
};
export const convertObjectDiscoveryToQueryStringSearch = (
  params: any,
): string => {
  if (!params) return '/';
  const newParams = cleanObject(params);
  return (
    '/search?' +
    Object.keys({ ...newParams })
      .map((key) => {
        return (
          (listingAndDiscoveryKeysMap[key] || key) +
          '=' +
          (listingAndDiscoveryValuesMap[newParams[key]] || newParams[key])
        );
      })
      .join('&')
      .concat('&router=productListing')
  );
};
export const convertGift = (gift: BenefitItems): any => {
  return {
    ...gift,
    image: {
      alt: gift.name,
      src: gift.imageUrl || `/api/products/${gift.sku}/default_image`,
    },
  };
};
export const convertCommonPromotion = (
  promotion: DiscoveryPromotion,
  price: number,
) => {
  let milestoneMoney = price;
  const promo: ProductPromotion = {
    id: promotion.id,
    endedAt: new Date(parseInt(promotion.endTimestampSec) * 1000).toISOString(),
    description: promotion.description,
    name: promotion.name,
    remainingTime: getRemainingTime(parseInt(promotion.endTimestampSec) * 1000),
    type: promotion?.condition?.coupon
      ? 'coupon'
      : promotion.applyOn === 'product'
      ? 'product'
      : 'order',
    condition: {
      orderValueMax: promotion.condition.orderValueMax,
      orderValueMin: promotion.condition.orderValueMin,
      coupon: promotion.condition.coupon,
      blockSize: promotion.condition.blockSize,
      minQuantity: promotion.condition.minQuantity,
    },
    benefit: {
      money: [],
      items: [],
      voucher: promotion?.benefit?.voucher,
    },
  };
  const gifts = promotion.benefit.gifts;
  if (gifts && Array.isArray(gifts)) {
    promo.benefit.items = gifts.map(convertGift);
  }
  const discount = promotion.benefit.discount;
  if (discount) {
    let discountMoney = 0;
    if (discount.percent) {
      if ((milestoneMoney * discount.percent) / 100 > discount.maxAmount) {
        discountMoney = discount.maxAmount;
      } else {
        discountMoney = (milestoneMoney * discount.percent) / 100;
      }
    } else {
      discountMoney = discount.flat;
    }
    const itemMoney: MoneyPromotion = {
      discountType: discount.percent ? 'percent' : 'money',
      maxDiscount: discount.maxAmount,
      id: promotion.id,
      percent: discount.percent,
      money: Math.round(discountMoney),
    };
    milestoneMoney = milestoneMoney - discountMoney;
    promo.benefit.money = [itemMoney];
  }
  promo.price = promotion?.price?.sellPrice
    ? +promotion?.price?.sellPrice
    : milestoneMoney;
  return { promotion: promo, price: milestoneMoney };
};
export const convertDefaultPromotions = (
  promotions: DiscoveryPromotion[],
  sellPrice: number,
): ProductPromotion[] => {
  let milestoneMoney = sellPrice;
  const convertedPromotions: ProductPromotion[] = [];
  for (let i = 0; i < promotions.length; i++) {
    const { promotion, price } = convertCommonPromotion(
      promotions[i],
      milestoneMoney,
    );
    milestoneMoney = price;
    convertedPromotions.push(promotion);
  }
  return convertedPromotions;
};
export const convertProductImage = (image: ProductDetailImage): any => {
  return {
    src: image.url,
    alt: image.label,
  };
};
export const convertProductStatus = (
  status: ProductStatus,
):
  | {
      status: string;
      color: string;
    }
  | undefined => {
  switch (status?.sellingCode) {
    case 'hang_trung_bay':
      return { status: t('Hàng trưng bày'), color: '#b4b4b4' };
    case 'hang_dat_truoc':
      return { status: t('Hàng đặt trước'), color: '#b4b4b4' };
    case 'ngung_kinh_doanh':
      return { status: t('Ngừng kinh doanh'), color: '#b4b4b4' };
    case 'hang_khac':
      return { status: t('Hàng quà tặng'), color: '#b4b4b4' };
    default:
      return undefined;
  }
};
export const convertStatusToProductTag = (
  status: ProductStatus,
): ProductTag | undefined => {
  switch (status?.sellingCode) {
    case 'hang_trung_bay':
      return { content: t('Hàng trưng bày'), variant: 'default' };
    case 'hang_dat_truoc':
      return { content: t('Hàng đặt trước'), variant: 'default' };
    case 'ngung_kinh_doanh':
      return { content: t('Ngừng kinh doanh'), variant: 'default' };
    case 'hang_khac':
      return { content: t('Hàng quà tặng'), variant: 'default' };
    default:
      return undefined;
  }
};
export const sortVariant = (
  variants: ProductDetailProductGroupVariant[],
  configurations: ProductDetailProductGroupConfiguration[],
): ProductDetailProductGroupVariant[] => {
  if (!variants || !configurations) {
    return null;
  }
  const configurationCodes = configurations.map((obj) => obj.code);
  const sortedVariants: ProductDetailProductGroupVariant[] = [];
  variants.forEach((variant) => {
    const temp: ProductDetailProductGroupVariant = {
      sku: variant.sku,
      attributeValues: [],
    };
    configurationCodes.forEach((code) => {
      const tmp = variant.attributeValues.findIndex(
        (att: ProductDetailProductGroupVariantAttrValue) => att.code === code,
      );
      if (tmp !== -1) {
        temp.attributeValues.push(variant.attributeValues[tmp]);
      }
    });
    sortedVariants.push(temp);
  });
  return sortedVariants;
};
export const convertConfigurations = (
  variants: ProductDetailProductGroupVariant[],
  configurations: ProductDetailProductGroupConfiguration[],
  sku: string,
): ConvertedConfiguration[] => {
  if (!variants || !configurations) return null;
  const newVariants: ProductDetailProductGroupVariantAttrValue[] = [];
  const newConfigurations: ConvertedConfiguration[] = [];
  variants.forEach((variant) => {
    (variant.attributeValues || []).forEach((attributeValue) => {
      newVariants.push(attributeValue);
    });
  });
  const productVariant = variants.find((variant) => variant.sku === sku);
  configurations.forEach((item, index: number) => {
    const optionsTmp: ConvertedConfiguration['options'] = [];
    (item.options || []).forEach((option) => {
      const tmp = newVariants.findIndex(
        (variant) => option.optionId === variant.optionId,
      );
      if (tmp !== -1) {
        const defaultVariant =
          productVariant &&
          productVariant.attributeValues.findIndex(
            (att) => att.optionId === option.optionId,
          );
        optionsTmp.push({
          value: option.value,
          optionId: option.optionId,
          visible: index === 0,
          selected: defaultVariant !== -1,
          thumbnailUrl: option.thumbnailUrl || '',
        });
      }
    });
    newConfigurations.push({
      ...item,
      options: optionsTmp,
    });
  });
  return newConfigurations;
};
export const validateVisibleVariant = (
  selectedVariant: ProductDetailProductGroupVariantAttrValue[],
  variants: ProductDetailProductGroupVariant[],
  configurations: ConvertedConfiguration[],
): ConvertedConfiguration[] => {
  if (!selectedVariant || !variants || !configurations) {
    return;
  }
  variants.forEach((variant) => {
    if (
      get(variant, 'attributeValues.0') &&
      variant.attributeValues[0].optionId === selectedVariant[0].optionId
    ) {
      let count = 0;
      variant.attributeValues.forEach((att, index: number) => {
        if (att.optionId === selectedVariant[index]?.optionId) {
          count++;
        }
      });
      if (count >= variant.attributeValues.length - 1) {
        variant.attributeValues.forEach((att, index: number) => {
          const temp = configurations[index].options.find(
            (config) => config.optionId === att.optionId,
          );
          if (temp && !temp.visible) {
            const pos = configurations[index].options.findIndex(
              (config) => config.optionId === att.optionId,
            );
            if (pos !== -1)
              configurations[index].options.splice(pos, 1, {
                ...temp,
                visible: true,
              });
          }
        });
      }
    }
  });
  return configurations;
};
export const convertFlashSale = (
  flashSales: DiscoveryFlashSale[],
): ConvertedProduct['flashSale'] => {
  const flashSale =
    !!flashSales.length &&
    flashSales.find(
      (fls) =>
        checkExpiryDateTimestamp(
          get(fls, 'startTimestampSec'),
          get(fls, 'endTimestampSec'),
        ) &&
        (get(fls, 'type') === 'HOT_DEAL' || get(fls, 'type') === 'GOLDEN_TIME'),
    );
  if (flashSale)
    return {
      total: flashSale.totalCount,
      used: flashSale.usedCount,
      endAt: new Date(parseInt(flashSale.endTimestampSec) * 1000).toISOString(),
      type: flashSale.type,
    };
  return null;
};
export const getImagesFromProduct = (
  product: DiscoveryProduct,
): ConvertedProduct['images'] => {
  if (!!product?.productDetail?.images?.length) {
    return product.productDetail.images.map(convertProductImage);
  }
  if (!!product.productInfo.imageUrl) {
    return [
      convertProductImage({
        url: product.productInfo.imageUrl,
        label: 'image',
      }),
    ];
  }
  return [];
};
export const convertProductDetail = (
  product: DiscoveryProduct,
): ConvertedProduct => {
  const status = convertProductStatus(product?.status);
  const productGroup = (product.productDetail.productGroup ||
    {}) as ProductDetailProductGroup;
  const variants = productGroup.variants || [];
  const configurations = productGroup.configurations || [];
  const sortedVariants = sortVariant(variants, configurations);
  const selected = sortedVariants.find(
    (variant) => variant.sku === product.productInfo.sku,
  )?.attributeValues;
  const convertedConfiguration =
    sortedVariants &&
    convertConfigurations(variants, configurations, product.productInfo.sku);
  const validatedConfiguration =
    convertedConfiguration &&
    validateVisibleVariant(selected, sortedVariants, convertedConfiguration);
  let selectionPromotions = product.selectionPromotions || [];
  if (!product?.selectionPromotions?.length) {
    selectionPromotions = product.promotions.filter(
      (el) =>
        !el.isDefault && el.applyOn === 'product' && !el?.condition?.coupon,
    ) as PromotionWithPrice[];
  }
  const newProduct = {
    sku: product.productInfo.sku,
    sellerSku: product.productInfo.sellerSku,
    flashSale: convertFlashSale(product.flashSales || []),
    status: product.status,
    images: getImagesFromProduct(product),
    title: {
      name: product.productInfo.name,
      ...(status && {
        status: {
          value: status.status,
          color: status.color,
        },
      }),
    },
    name: product.productInfo.name,
    displayName: product.productInfo.name,
    price: {
      terminalPrice: get(product, 'prices.0.terminalPrice'),
      supplierRetailPrice: get(product, 'prices.0.supplierRetailPrice'),
      sellPrice: get(product, 'prices.0.sellPrice'),
      priceWithoutSelectionPromotion: product.priceWithoutSelectionPromotion,
      ...convertPrice(product),
    },
    seoInfo: get(product, 'productDetail.seoInfo'),
    brand: product.productInfo.brand,
    attributeGroups: product.productDetail.attributeGroups,
    attributes: [] as any,
    needManageStock: product.totalAvailable === null ? false : true,
    totalAvailable: product.totalAvailable,
    selectionPromotions: product.selectionPromotions || [],
    categories: product.productInfo.categories,
    masterCategories: product.productInfo.masterCategories,
    attributeSet: product.productDetail.attributeSet,
    defaultPromotions: product.defaultPromotions || [],
    productGroup: {
      ...product.productDetail.productGroup,
      variants: sortedVariants || [],
      configurations: validatedConfiguration || [],
    },
    orderCouponPromotions: (product.promotions || []).filter(
      (el) => el.applyOn === 'order' && !el.isDefault,
    ),
    partOrderPromotions: (product.promotions || []).filter(
      (el) =>
        el.applyOn === 'part_order' && el.isDefault && !!el.condition.coupon,
    ),
    provider: product.provider,
    seller: product.productInfo.seller,
    selected,
    isBundle: false,
    tags: product.productInfo.tags,
    save: get(product, 'prices.0.discountAmount', 0),
    saveWithoutSelectionPromotion: product.priceWithoutSelectionPromotion
      ? Number(get(product, 'prices.0.supplierRetailPrice', 0)) -
        product.priceWithoutSelectionPromotion
      : get(product, 'prices.0.discountAmount', 0),
    selectPromotionId: selectionPromotions?.[0]?.id,
    isAlcoholicCategory: product.isAlcoholicCategory,
    uomName: product.productInfo.uomName,
    uomRatio: product.productInfo.uomRatio,
    clearanceTypeDescription: product?.productDetail?.clearanceTypeDescription,
  };
  if (newProduct.price.price === newProduct.price.listedPrice) {
    newProduct.price.listedPrice = undefined;
  }
  return newProduct;
};
export const isValidPrice = (product: DiscoveryProduct) => {
  const sellPrice = product?.prices?.[0]?.sellPrice;
  const latestPrice = product?.prices?.[0]?.latestPrice;
  if (sellPrice === null || latestPrice === null) return false;
  const sellPriceNumber = Number(sellPrice);
  const latestPriceNumber = Number(latestPrice);
  if (isNaN(sellPriceNumber) || isNaN(latestPriceNumber)) return false;
  return sellPriceNumber > 0 && latestPriceNumber >= 0;
};

const getDisplayPrice = (
  discoveryPrice: DiscoveryPrice,
  isFlashSale: boolean,
): ProductPriceProps => {
  if (!discoveryPrice) return;

  let priceRange: ProductPriceProps['priceRange'];
  const minLatestPrice = parseFloat(discoveryPrice.minLatestPrice);
  const maxLatestPrice = parseFloat(discoveryPrice.maxLatestPrice);
  const latestPrice = parseFloat(discoveryPrice.latestPrice);

  if (!isNaN(minLatestPrice) && !isNaN(maxLatestPrice) && !isNaN(latestPrice)) {
    if (
      minLatestPrice !== 0 &&
      maxLatestPrice !== 0 &&
      (minLatestPrice !== latestPrice || maxLatestPrice !== latestPrice)
    ) {
      priceRange = {
        min: minLatestPrice,
        max: maxLatestPrice,
      };
    }
  }

  return {
    latestPrice: +discoveryPrice.latestPrice,
    supplierRetailPrice: discoveryPrice.discountPercent
      ? +discoveryPrice.supplierRetailPrice
      : undefined,
    discountPercent:
      discoveryPrice.discountPercent && !isFlashSale
        ? `-${discoveryPrice.discountPercent}%`
        : undefined,
    discountAmount:
      +discoveryPrice.discountAmount > 0 && !isFlashSale
        ? +discoveryPrice.discountAmount
        : undefined,
    priceRange,
  };
};
export class ProductHelper {
  milestoneShippingFee?: number;
  constructor(milestoneShippingFee = null) {
    this.milestoneShippingFee = milestoneShippingFee;
  }
  getLinkProduct(product: DiscoveryProduct, skuVariant?: string): string {
    const { productInfo } = product;
    let sku = productInfo.sku;
    if (skuVariant) {
      sku = skuVariant;
    }
    return productInfo.slug
      ? `/${productInfo.slug}--s${sku}`
      : `/${slug(productInfo.name)}--s${sku}`;
  }
  hasGift(promotions: DiscoveryPromotion[]): boolean {
    let items: BenefitItems[] = [];
    promotions.forEach((promotion) => {
      if (Array.isArray(promotion.benefit.gifts)) {
        items = items.concat(get(promotion, 'benefit.gifts', []));
      }
    });
    return !!items.length;
  }
  convertProductDetail(product: DiscoveryProduct): ConvertedProduct {
    return convertProductDetail(product);
  }
  getBestPrice(product: ConvertedProduct, selectedPromotionId: number): number {
    const promotion = (product?.selectionPromotions || []).find(
      (promotion) => promotion.id === selectedPromotionId,
    );
    if (!promotion) {
      return product?.price?.price as number;
    }
    return (promotion?.price?.sellPrice ||
      promotion?.price?.supplierSalePrice) as number;
  }
  convertNewProductCard = (
    product: DiscoveryProduct,
    options?: OptionConvertProductCart,
  ): NewProductCardProps => {
    const {
      productInfo,
      promotions,
      totalAvailable,
      prices,
      flashSales,
      status,
      pos365StockInfo,
    } = product;
    const { frames, cart, hasBrand, showUom, contactLink } = options || {};
    let hasGift = false;
    let freeship = false;
    const productPrice = prices?.[0];
    // Hiển thị icon freeship nếu đơn hàng lớn hơn hoặc bằng milestoneShippingFee
    if (
      this.milestoneShippingFee &&
      this.milestoneShippingFee >= 0 &&
      productPrice?.latestPrice &&
      parseInt(productPrice?.latestPrice) >= this.milestoneShippingFee
    )
      freeship = true;
    promotions.forEach((el) => {
      if (
        el.isDefault &&
        el.applyOn === PromotionApplyOnEnum.product &&
        (el?.benefit?.gifts || []).length
      )
        hasGift = true;
    });
    const optionPromotions = promotions.filter(
      (el) => !el.isDefault && el.applyOn === PromotionApplyOnEnum.product,
    );
    if (
      optionPromotions.length &&
      optionPromotions[0] &&
      optionPromotions[0].benefit?.gifts.length
    )
      hasGift = true;
    let flashSale;
    let isFlashSale = false;
    const flSale = flashSales[0];
    if (flashSales.length) {
      isFlashSale = true;
      flashSale = {
        endAt: getTime(flSale.endTimestampSec),
        text:
          flSale.totalCount - flSale.usedCount < 5
            ? t('Còn lại {{var0}}', {
                var0: flSale.totalCount - flSale.usedCount,
              })
            : t('Đã bán {{FlSaleUsedCount}}', {
                FlSaleUsedCount: flSale.usedCount,
              }),
        discountPercent: Math.round(productPrice.discountPercent),
      };
    }
    let addToCart;
    let ctaButton: ProductCardButton = undefined;
    let contactButton: ProductCardButton = undefined;
    const showContact =
      product.totalAvailable === 0 ||
      !isValidPrice(product) ||
      !product?.status?.sellable;
    if (cart) {
      const orderLines = cart.order?.orderLines || [];
      const currentOrderLine = orderLines.find(
        (orderline: CartOrderLine) => orderline.sku === productInfo.sku,
      );
      const optionPromotions = promotions.filter(
        (el) =>
          !el.isDefault &&
          el.applyOn === PromotionApplyOnEnum.product &&
          el.type === 'product',
      );
      const quantityInCart =
        (currentOrderLine && currentOrderLine.quantity) || 0;
      addToCart = {
        onAddToCart: async (quantity = 1) => {
          await cart.onAddToCart(
            {
              sku: productInfo.sku,
              quantity: quantity,
              selectPromotionId: optionPromotions[0]?.id,
            },
            true,
          );
        },
        onIncrease: async () => {
          await cart.onIncrease(currentOrderLine);
        },
        onDecrease: async () => {
          if (quantityInCart <= 1) {
            await cart.onRemoveItem(currentOrderLine);
          } else {
            await cart.onDecrease(currentOrderLine);
          }
        },
        onChangeQuantity: async (newQuantity: number) => {
          await cart.onChangeQuantity(currentOrderLine, newQuantity);
        },
        quantityInCart,
      };
      if (!showContact) {
        ctaButton = {
          children: t('Thêm vào giỏ'),
          variant: 'secondary',
          onClick: (e) => {
            e.preventDefault();
            addToCart.onAddToCart();
          },
        };
      } else {
        if (contactLink)
          contactButton = {
            children: t('Liên hệ'),
            variant: 'secondary',
            onClick: (e) => {
              e.preventDefault();
              window.open(contactLink);
            },
          };
        else
          ctaButton = {
            children: t('Hết hàng'),
            disabled: true,
          };
      }
    }
    let titleTag: ProductTag = null;
    const isOutOfStock = product.totalAvailable === 0;
    if (isOutOfStock) {
      titleTag = {
        variant: 'link',
        content: t('Hàng sắp về'),
      };
    }
    const price = convertPrice(product);
    if (!price?.price) {
      titleTag = {
        variant: 'pending',
        content: t('Liên hệ'),
      };
    }
    const infoTags = [];
    if (status && convertStatusToProductTag(status)) {
      infoTags.push(convertStatusToProductTag(status));
    }
    const quantity =
      totalAvailable === null
        ? 1000
        : isFlashSale
        ? Math.min(flSale.totalCount - flSale.usedCount, totalAvailable)
        : totalAvailable;
    const { name, code } = productInfo.brand;

    return {
      sku: productInfo.sku,
      name: productInfo.name,
      imageUrl: productInfo.imageUrl,
      price: getDisplayPrice(productPrice, isFlashSale),
      stockQuantity: quantity,
      stockQuantityText:
        !isFlashSale && !isOutOfStock && quantity < 5
          ? t('Còn {{quantity}} sản phẩm', { quantity: quantity })
          : undefined,
      warranty: {
        months: productInfo.warranty.months,
      },
      freeship,
      flashSale: flashSale,
      addToCart,
      frame: frames?.length
        ? getFrame(productInfo.tags, frames || [])
        : undefined,
      hasGift,
      titleTag,
      infoTags,
      link: {
        as: {
          pathname: this.getLinkProduct(product),
          query: {
            sku: productInfo.sku,
          },
        },
        href: {
          pathname: PRODUCT_DETAIL_PAGE,
          query: { sku: productInfo.sku },
        },
        passHref: true,
      },
      ...(hasBrand && {
        brand: {
          name: name,
          onClick: () => {
            window.location.href = `/${code}-brand.${code}`;
          },
        },
      }),
      ctaButton,
      contactButton,
      needConfirmAge: needConfirm18YearsOld(product?.isAlcoholicCategory),
      ...(showUom &&
        productInfo.uomRatio > 0 && {
          uom: {
            uomName: productInfo.uomName,
            uomRatio: productInfo.uomRatio,
          },
        }),
      ...pos365StockInfo,
    };
  };
  convertProductCard = (
    product: DiscoveryProduct,
    options?: OptionConvertProductCart,
  ): ProductCardProps & {
    selectPromotionId: number;
  } => {
    const { productInfo, promotions, totalAvailable, prices, flashSales } =
      product;
    const { frames, cart } = options || {};
    const gifts = [];
    const icons = [];
    // Hiển thị icon freeship nếu đơn hàng lớn hơn hoặc bằng milestoneShippingFee
    if (
      this.milestoneShippingFee &&
      get(prices, '0.latestPrice') &&
      get(prices, '0.latestPrice') >= this.milestoneShippingFee
    )
      icons.push({ name: 'freeshipping-color', color: '#61c94d', size: 22 });
    promotions.forEach((el) => {
      if (
        el.isDefault &&
        el.applyOn === 'product' &&
        (el?.benefit?.gifts || []).length
      ) {
        gifts.push(
          ...(el?.benefit?.gifts || []).map((item) => ({
            src: item.imageUrl,
            alt: item.name,
            sku: item.sku,
          })),
        );
      }
    });
    const optionPromotions = promotions.filter(
      (el) => !el.isDefault && el.applyOn === 'product',
    );
    const selectPromotionId = optionPromotions[0]?.id;
    if (
      optionPromotions.length &&
      optionPromotions[0] &&
      (optionPromotions[0].benefit?.gifts || []).length
    ) {
      gifts.push(
        ...(optionPromotions[0].benefit?.gifts || []).map((item) => ({
          src: item.imageUrl,
          alt: item.name,
          sku: item.sku,
        })),
      );
    }
    let flashSale;
    if (flashSales.length && flashSales[0]) {
      flashSale = {
        endAt: new Date(parseInt(flashSales[0].endTimestampSec) * 1000),
        value: flashSales[0].usedCount,
        total: flashSales[0].totalCount,
      };
    }
    let addToCart;
    if (cart) {
      const orderLines = get(cart, 'order.orderLines', []);
      const currentOrderLine = orderLines.find(
        (orderline: CartOrderLine) => orderline.sku === productInfo.sku,
      );
      const quantityInCart =
        (currentOrderLine && currentOrderLine.quantity) || 0;
      addToCart = {
        onAddToCart: async () => {
          await cart.onAddToCart(
            {
              sku: productInfo.sku,
              quantity: 1,
              selectPromotionId,
            },
            true,
          );
          cart.refetch();
        },
        onIncrease: async () => {
          await cart.onIncrease(currentOrderLine);
        },
        onDecrease: async () => {
          if (quantityInCart < 2) {
            await cart.onRemoveItem(currentOrderLine);
          } else {
            await cart.onDecrease(currentOrderLine);
          }
          cart.refetch();
        },
        onChangeQuantity: async (newQuantity: number) => {
          await cart.onChangeQuantity(currentOrderLine, newQuantity);
        },
        quantityInCart,
      };
    }
    let tag: TagProps = null;
    const isOutOfStock = product.totalAvailable === 0;
    if (isOutOfStock) {
      tag = {
        variant: 'link',
        children: t('Hàng sắp về'),
      };
    }
    const price = convertPrice(product);
    if (!price?.price) {
      tag = {
        variant: '#4F49D9',
        children: t('Liên hệ'),
      };
    }
    return {
      frame: frames?.length
        ? getFrame(productInfo.tags, frames || [])
        : undefined,
      link: {
        as: {
          pathname: this.getLinkProduct(product),
          query: {
            sku: productInfo.sku,
          },
        },
        href: { pathname: '/products/[sku]', query: { sku: productInfo.sku } },
        passHref: true,
      },
      tag,
      tags: [],
      image: {
        src: productInfo.imageUrl,
      },
      title: {
        name: productInfo.name,
      },
      price: {
        price: get(prices, '0.latestPrice'),
        listedPrice: get(prices, '0.supplierRetailPrice'),
        terminalPrice: get(product, 'prices.0.terminalPrice'),
        sellPrice: get(prices, '0.sellPrice'),
        discount: get(prices, '0.discountPercent')
          ? `-${get(prices, '0.discountPercent')}%`
          : undefined,
      },
      icons,
      ...(gifts.length > 0 && {
        gifts: {
          label: t('Tặng ngay'),
          images: gifts.map((gift: any) => ({
            src: gift.imageURL || `/api/products/${gift.sku}/default_image`,
            alt: gift.name,
            sku: gift.sku,
          })),
        },
      }),
      flashsale: flashSale,
      sku: productInfo.sku,
      totalAvailableByStocks:
        totalAvailable === null ? 1000 : totalAvailable || 0,
      displayStatus: DisplayStatus.BUYABLE,
      showContact:
        isOutOfStock || !isValidPrice(product) || !product?.status?.sellable,
      addToCart,
      selectPromotionId,
    };
  };
}
export const productHelper = new ProductHelper();
const cookDiscoveryFilter = (filter: DiscoveryFilter) => {
  if (!filter) return {};
  if (filter?.attributes.length) {
    filter.attributes.map(
      (el) => (filter[`attributes.${el.code}`] = el.optionIds.join()),
    );
    const { attributes: tbr, ...newFilter } = filter;
    return newFilter;
  }
  return filter;
};
export const findBlockByType = (
  type: string,
  blocks: DiscoveryBlock[],
): DiscoveryBlock => {
  return blocks && blocks.find((block) => block.type === type);
};
const getUrl = (item: DiscoveryItem): string => {
  let url = '';
  if (item.url) {
    url = item.url;
  } else {
    url = convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(item?.params?.filter),
      ...get(item, 'params.sorting', {}),
    });
  }
  return url;
};
const getLinkHref = (
  url: string,
  paramsObject: {},
  title: string,
): LinkProps => {
  let href = null;
  if (url) {
    if (url.startsWith('/landing/')) {
      href = {
        pathname: '/landing/[id]',
        query: {
          id: url.substring('/landing/'.length),
        },
      };
    } else {
      href = url;
    }
  } else {
    href = {
      pathname: '/search',
      query: {
        ...paramsObject,
        pageTitle: title,
        router: 'productListing',
      },
    };
  }
  return { href };
};
export const transformBlockItemTypeSku = (block: DiscoveryBlock) => {
  const { fetchParams } = block.content;
  block.cookedData = {
    queryString: convertObjectDiscoveryToQueryStringSearch({
      ...cookDiscoveryFilter(fetchParams?.filter),
      ...fetchParams.sorting,
    }),
    paramsObject: cleanObject({
      ...cookDiscoveryFilter(fetchParams?.filter),
      ...(fetchParams.sorting || {}),
      blockId: block.id,
    }),
  };
  return block;
};
export const transformBanner = (block: DiscoveryBlock) => {
  const banners: BannerEntity[] = block.content.items.map((item) => {
    const paramsObject = cleanObject({
      ...cookDiscoveryFilter(item?.params?.filter),
      ...get(item, 'params.sorting', {}),
      blockId: block.id,
      itemId: item.id,
    });
    return {
      id: item.id,
      targetPath: getUrl(item),
      image: item.imageUrl,
      campaignName: item.label,
      description: item.subLabel,
      paramsObject,
      url: item.url,
      thumbnail: item.thumbnail,
      link: getLinkHref(item.url, paramsObject, item.label),
      imageSize: item.imageSize,
    };
  });
  block.cookedData = banners || [];
  return block;
};
export const transformBlockMixBanner = (block: DiscoveryBlock) => {
  const firstItem = block.content.items[0];
  const trackingRegionName = 'categoriesBlockBrand';
  block.cookedData = {
    header: {
      title: block.label,
      link: {
        href: getUrl(firstItem),
      },
    },
    banner: {
      src: firstItem.imageUrl,
      alt: firstItem.label || firstItem.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: firstItem.label,
        target: getUrl(firstItem),
      }),
      link: {
        href: getUrl(firstItem),
      },
    },
    contentItems: block.content.items.slice(1, 5).map((category) => ({
      name: category.label,
      description: category.subLabel,
      image: {
        src: category.imageUrl,
        alt: category.label || category.subLabel,
      },
      link: {
        href: getUrl(category),
      },
    })),
    bottomImages: block.content.items.slice(5, 11).map((brand, index) => ({
      src: brand.imageUrl,
      alt: brand.label || brand.subLabel,
      tracking: getTrackingData({
        isTrackContent: true,
        regionName: trackingRegionName,
        contentName: brand.label,
        index,
        target: getUrl(brand),
      }),
      link: {
        href: getUrl(brand),
      },
    })),
  } as PromoteBlockProps;
  return block;
};
export const transformBlockHotDeal = (block: DiscoveryBlock) => {
  block.cookedData = block.content.items.map((el: any) => {
    return {
      ...el,
      name: el.label,
      paramsObject: cleanObject({
        ...cookDiscoveryFilter(el?.params?.filter),
        ...get(el, 'params.sorting', {}),
        blockId: block.id,
        itemId: el.id,
      }),
    };
  });
  return block;
};
export const transformBlockListingTab = (block: DiscoveryBlock) => {
  const categoryList: any[] = [];
  block.content.items.forEach((item: DiscoveryItem) => {
    const obj = {
      id: item.id,
      name: item.label,
      paramsObject: cleanObject({
        ...cookDiscoveryFilter(item?.params?.filter),
        ...get(item, 'params.sorting', {}),
        blockId: block.id,
        itemId: item.id,
      }),
    };
    categoryList.push(obj);
  });
  block.cookedData = categoryList;
  return block;
};
export const transformBlock = (block: DiscoveryBlock) => {
  if (block.itemType === 'SKU') return transformBlockItemTypeSku(block);
  else if (
    block.itemType === 'BANNER' ||
    [
      'HORIZONTAL_HIGHLIGHTED_BANNER',
      'RECOMMENDED_CATEGORY',
      'RECOMMENDED_BRANDS',
      'PROMOTE_CATEGORY_MENU',
      'HORIZONTAL_2_BANNER_MOBILE',
    ].includes(block.type)
  )
    return transformBanner(block);
  else if (block.type === 'MIX_BANNER') return transformBlockMixBanner(block);
  else if (
    block.type === 'COUNT_DOWN_PROMO_BIG_SKU' ||
    block.type === 'COUNT_DOWN_PROMO_TAB_MOBILE'
  )
    return transformBlockHotDeal(block);
  else if (block.type === 'LISTING_TAB') return transformBlockListingTab(block);
  else return block;
};
export const getNewVariant = (
  product: DiscoveryProduct,
  variant: TProductVariant,
  index: number,
): string => {
  const convertedProduct = convertProductDetail(product);
  if (!convertedProduct?.selected?.length) return;
  const selectedVariants: ProductDetailProductGroupVariantAttrValue[] = [];
  const att: ProductDetailProductGroupVariantAttrValue = {
    code: convertedProduct.productGroup.configurations[index].code,
    value: variant.value,
    id: variant.optionId,
    optionId: variant.optionId,
  };
  for (
    let i = 0;
    i < convertedProduct.productGroup.configurations.length;
    i++
  ) {
    if (convertedProduct.selected[i]?.code === att.code) {
      selectedVariants.push(att);
    } else {
      selectedVariants.push(convertedProduct.selected[i]);
    }
  }
  if (!selectedVariants.length) {
    return;
  }
  let max = 0;
  let newSku = '';
  convertedProduct.productGroup.variants.forEach((variant) => {
    let countAtt = 0;
    for (let i = 0; i < variant.attributeValues.length; i++) {
      if (
        variant.attributeValues[i]?.optionId === selectedVariants[i]?.optionId
      ) {
        countAtt++;
      } else {
        break;
      }
    }
    if (countAtt > max) {
      max = countAtt;
      newSku = variant.sku;
    }
  });
  if (newSku && newSku !== convertedProduct.sku) return newSku;
};
